import { assetPrefix } from 'constants/app';
import type {
  LanguageCode,
  ResolutionString,
  TradingTerminalWidgetOptions,
} from '../../../../public/tradingview/charting_library';
import { getLanguageFromURL, optionalArray } from './utils';
import { GetWidgetOptionsProps } from '../types';
import { handleBackgroundColor, handleCustomCss } from '../utils';

const getTradingViewWidgetOptions = ({
  isDark,
  isMobile,
  isDesktop,
  newChartbit,
}: GetWidgetOptionsProps): Omit<
  TradingTerminalWidgetOptions,
  'datafeed' | 'symbol'
> => ({
  interval: newChartbit ? ('1D' as ResolutionString) : null,
  loading_screen: {
    foregroundColor: '#00ab6b',
    backgroundColor: handleBackgroundColor(isDark, isDesktop),
  },
  timezone: 'Asia/Jakarta',
  container: 'tv_chart_container',
  library_path: isDesktop
    ? 'https://stockbit.com/tradingview/charting_library/'
    : `${assetPrefix}/tradingview/charting_library/`,
  locale: (getLanguageFromURL() || 'en') as LanguageCode,
  charts_storage_api_version: '1.1',
  custom_css_url: `../../tradingview/styles/custom${
    isMobile ? '-mobile' : isDesktop ? '-desktop' : ''
  }.css`,
  custom_font_family: isDesktop ? isDesktop.fontFamily : "'ProximaNovaRg'",
  client_id: 'tradingview.com',
  user_id: 'public_user_id',
  study_count_limit: 100,
  disabled_features: [
    'trading_account_manager',
    'custom_resolutions',
    'display_market_status',
    'create_volume_indicator_by_default',
    'go_to_date',
    'right_toolbar',
    'header_quick_search',
    ...optionalArray(!!isMobile, [
      ...optionalArray(
        typeof isMobile === 'object' && !isMobile.showSearchBar,
        ['header_symbol_search', 'header_compare'],
      ),
      'header_settings',
      'header_screenshot',
      'header_fullscreen_button',
      'source_selection_markers',
    ]),
    ...optionalArray(!newChartbit, [
      'header_saveload',
      'save_shortcut',
      'support_multicharts',
      'header_layouttoggle',
      'drawing_templates',
    ]),
    ...optionalArray(!!isDesktop, [
      'header_screenshot',
      'header_symbol_search',
    ]),
  ],
  enabled_features: [
    'use_localstorage_for_settings',
    'show_average_close_price_line_and_label',
    'symbol_search_hot_key',
    ...optionalArray(!!isMobile, [
      'hide_left_toolbar_by_default',
      'iframe_loading_compatibility_mode',
    ]),
    ...optionalArray(newChartbit, [
      'chart_template_storage',
      'study_templates',
      'saveload_separate_drawings_storage',
    ]),
  ],
  autosize: true,
  auto_save_delay: newChartbit ? 5 : 1,
  overrides: {
    // main chart
    'paneProperties.topMargin': 17,
    'paneProperties.bottomMargin': 15,
    'paneProperties.legendProperties.showStudyValues': false,
    'paneProperties.legendProperties.showLegend': true,

    // main series
    'mainSeriesProperties.candleStyle.upColor': isDark ? '#53b987' : '#009900',
    'mainSeriesProperties.candleStyle.downColor': isDark
      ? '#eb4d5c'
      : '#B80E0C',
    'mainSeriesProperties.candleStyle.wickUpColor': isDark
      ? '#336854'
      : '#000000',
    'mainSeriesProperties.candleStyle.wickDownColor': isDark
      ? '#7f323f'
      : '#000000',
    'mainSeriesProperties.candleStyle.borderColor': isDark
      ? '#378658'
      : '#000000',
    'mainSeriesProperties.candleStyle.borderUpColor': isDark
      ? '#53b987'
      : '#000000',
    'mainSeriesProperties.candleStyle.borderDownColor': isDark
      ? '#eb4d5c'
      : '#000000',

    'scalesProperties.showStudyLastValue': true,
    'scalesProperties.showSeriesLastValue': true,

    // drawing
    'linetoolfibretracement.coeffsAsPercents': true,
    'linetoolfibchannel.coeffsAsPercents': true,
    'linetoolfibcircles.coeffsAsPercents': true,
    'linetooltrendbasedfibextension.coeffsAsPercents': true,
    'linetoolray.linewidth': 2,
    'linetoolfibretracement.extendLines': true,
    'linetoolfibretracement.transparency': 90,
    'linetoolfibretracement.horzLabelsAlign': 'right',
    'linetoolfibretracement.vertLabelsAlign': 'bottom',
    'linetoolfibretracement.showPrices': false,
  },
  time_frames: [
    {
      text: '1d',
      resolution: '5' as ResolutionString,
      description: '1 Day',
      title: '1D',
    },
    {
      text: '5d',
      resolution: '5' as ResolutionString,
      description: '5 Days',
      title: '5D',
    },
    {
      text: '1m',
      resolution: 'D' as ResolutionString,
      description: '1 Month',
      title: '1M',
    },
    {
      text: '3m',
      resolution: 'D' as ResolutionString,
      description: '3 Months',
      title: '3M',
    },
    {
      text: '6m',
      resolution: 'D' as ResolutionString,
      description: '6 Months',
      title: '6M',
    },
    {
      text: '1y',
      resolution: 'D' as ResolutionString,
      description: '1 Year',
      title: '1Y',
    },
    {
      text: '5y',
      resolution: 'D' as ResolutionString,
      description: '5 Years',
      title: '5Y',
    },
    {
      text: '1000y',
      resolution: 'D' as ResolutionString,
      description: 'All',
      title: 'All',
    },
  ],
  widgetbar: {
    datawindow: true,
  },
});

const INTRADAY_RESOLUTION = [
  '1' as ResolutionString,
  '3' as ResolutionString,
  '5' as ResolutionString,
  '15' as ResolutionString,
  '30' as ResolutionString,
  '45' as ResolutionString,
  '60' as ResolutionString,
  '120' as ResolutionString,
  '180' as ResolutionString,
  '240' as ResolutionString,
];

const DAILY_RESOLUTION = [
  '1D' as ResolutionString,
  '1W' as ResolutionString,
  '1M' as ResolutionString,
  '3M' as ResolutionString,
  '6M' as ResolutionString,
  '12M' as ResolutionString,
];

const generateResolution = (
  from: number,
  to: number,
  resolution?: 'H' | 'D' | 'W' | 'M',
): ResolutionString[] => {
  const data = [];
  for (let i = from; i <= to; i += 1) {
    data.push(`${i}${resolution || ''}`);
  }
  return data;
};

const ALL_RESOLUTIONS = [
  // Intraday
  ...generateResolution(1, 1440), // 1 Minute - 24 Hours
  ...generateResolution(1, 24, 'H'), // 1H = 24H
  // DWM
  ...generateResolution(1, 365, 'D'), // 1D - 365D
  ...generateResolution(1, 52, 'W'), // 1W - 52W
  ...generateResolution(1, 12, 'M'), // 1M - 12M
];

const TV_IDENTIFIERS = {
  DIALOG_COMPARE_SELECTOR: '[data-name="compare-dialog"]',
  DIALOG_SETTINGS_SELECTOR: '[data-name="series-properties-dialog"]',
  DIALOG_SYMBOL_SEARCH_SELECTOR: '[data-name="symbol-search-items-dialog"]',
  HEADER_TOOLBAR_UNDO_REDO_SELECTOR:
    '[data-name="header-toolbar-undo-redo"],#header-toolbar-undo-redo',
  TIMEZONE_LIST_ID: 'overlap-manager-root',
  TIMEZONE_LIST_SELECTOR: '[data-name="menu-inner"] table',
  CAMERA_MENU_SELECTOR: '[data-name="save-chart-image"]',
  LOAD_LAYOUT_MENU_SELECTOR: '[data-name="save-load-menu-item-load"]',
  CHART_CONTAINER: 'tv_chart_container',
  CUSTOM_SAVE_BUTTON_ICON: '#custom-save-button',
  HEADER_TOOLBAR_TRADE_BUTTON_SLOT: 'trade-button-slot',
  CUSTOM_TEMPLATES_BUTTON: '#custom-templates-button',
  THEME_TOGGLER_BUTTON: 'theme-toggler-button',
  CUSTOM_INDICATORS_BUTTON: '#custom-indicators-button',
  INDICATORS_BUTTON: '#header-toolbar-indicators',
  CUSTOM_SETTINGS_BUTTON: 'custom-settings-button',
  BODY: '.chart-page',
  TIMEFRAME_ALL: '[data-name="date-range-tab-12000M"]',
  INDICATOR_TEMPLATES_BUTTON: '#header-toolbar-study-templates',
  SYNC_DRAWING: '[data-name="SyncDrawing"]',
  SAVE_LOAD_MENU: '[data-name="save-load-menu"]',
  MY_INVESTMENT_BUTTON_ICON: 'my_investment_pie_chart',
};

export const nonIndicatorApiFormula = ['VALUE', 'FREQUENCY'];
export const formulaToPriceDataObjKey = {
  [nonIndicatorApiFormula[0]]: 'value',
  [nonIndicatorApiFormula[1]]: 'frequency',
};

export {
  getTradingViewWidgetOptions,
  INTRADAY_RESOLUTION,
  DAILY_RESOLUTION,
  ALL_RESOLUTIONS,
  TV_IDENTIFIERS,
};
